/**
 * Money given back by a non-cash method (Phase 7A-2C, ADR-029).
 *
 * After a GCash or bank-transfer give-back the POS asks for the reference of the money sent; it is
 * optional then and can be added later from the order's money panel, or by the owner when checking.
 * A non-cash payment void is answered "Money sent back" or "The payment never arrived". Both are
 * statements of their own (`cash.give_back_reference`, `cash.give_back_outcome`), never part of the
 * refund or the void, so the approval and its 2-minute grant are unchanged.
 *
 * A transfer is what the GCash check lists as one row of money sent: the rows of one refund with the
 * same method (a refund split over two tenders is one transfer), or one void. The server groups them
 * (`app.reversal_transfer_id`); the id of a transfer is the smallest reversal id among its rows.
 */

export const GIVE_BACK_OUTCOMES = ['sent_back', 'never_arrived'] as const;
export type GiveBackOutcome = (typeof GIVE_BACK_OUTCOMES)[number];

export const GIVE_BACK_OUTCOME_LABELS: Record<GiveBackOutcome | 'withdrawn', string> = {
  sent_back: 'Money sent back',
  never_arrived: 'The payment never arrived',
  withdrawn: 'Withdrawn by the owner',
};

/** What the question after a non-cash void explains for each answer. */
export const GIVE_BACK_OUTCOME_HELP: Record<GiveBackOutcome, string> = {
  sent_back: 'The money came in and was sent back to the customer.',
  never_arrived: 'Nothing came in, so nothing was sent back.',
};

export const GIVE_BACK_REFERENCE_MAX = 64;

/** One give-back of an order, as `public.order_give_backs` returns it to a tablet. */
export interface GiveBackView {
  /** The transfer's id: the smallest reversal id among its rows. */
  transfer_id: string;
  kind: 'refund' | 'void';
  reversal_ids: string[];
  /** The tender a void reverses. */
  payment_id: string | null;
  method: string;
  method_kind: string;
  amount_minor: number;
  recorded_at: string;
  reference_last4: string | null;
  /** A void's answer; `withdrawn` is the owner's withdrawal of a missed payment (ADR-028). */
  outcome: GiveBackOutcome | 'withdrawn' | null;
  cancelled: boolean;
  /** Whether the person asking may give the reference now. */
  can_reference: boolean;
  /** Whether the person asking may answer the void now. */
  can_answer: boolean;
}

/** The give-backs a money command just made (by its reversal ids), as the server grouped them. */
export function giveBacksOf(
  all: readonly GiveBackView[],
  reversalIds: readonly string[],
): GiveBackView[] {
  const ids = new Set(reversalIds);
  return all.filter((g) => g.reversal_ids.some((id) => ids.has(id)));
}

/** Whether the question after a give-back has anything left to ask this person. */
export function giveBackAsks(g: GiveBackView): { answer: boolean; reference: boolean } {
  const answer = g.kind === 'void' && g.can_answer && g.outcome === null;
  const reference =
    g.can_reference &&
    g.reference_last4 === null &&
    !g.cancelled &&
    g.outcome !== 'never_arrived' &&
    g.outcome !== 'withdrawn';
  return { answer, reference };
}

/** The reference as the server stores it: trimmed, 1 to 64 characters; `null` when empty. */
export function normalizeGiveBackReference(input: string): string | null {
  const v = input.trim();
  return v.length === 0 ? null : v;
}

export function validateGiveBackReference(input: string): string | null {
  const v = normalizeGiveBackReference(input);
  if (v === null) return 'Type the reference of the money sent';
  if (v.length > GIVE_BACK_REFERENCE_MAX)
    return `A reference is at most ${String(GIVE_BACK_REFERENCE_MAX)} characters`;
  return null;
}

/** Refusal codes the server attaches to a statement (`hint.give_back`). */
export type GiveBackRefusal =
  | 'shift_closed'
  | 'owner_has_it'
  | 'no_change'
  | 'not_non_cash'
  | 'not_transfer'
  | 'cancelled'
  | 'never_arrived'
  | 'withdrawn'
  | 'refunded'
  | 'settled'
  | 'not_void';

export const GIVE_BACK_REFUSAL_TEXT: Partial<Record<GiveBackRefusal, string>> = {
  shift_closed: 'That shift is closed: the owner adds it when checking.',
  owner_has_it: 'The owner has this one now: ask the owner.',
  no_change: 'That is already recorded.',
  refunded: 'Part of that payment was refunded, so it arrived: the void sent the rest back.',
  settled: 'That payment is settled as a loss: the owner undoes the settlement first.',
  never_arrived: 'Nothing was sent for a payment that never arrived.',
};
